import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { liftChip } from "../../lib/compositionModel.js";
import { EntrySparkline } from "../SidebarNavRuntime.jsx";

// Rows shown per composition panel (top-N members of a dim within scope).
export const COMPOSITION_ROWS = 5;

// The baseline-lift badge — how much a member over-/under-indexes on this scope
// vs the org (see computeLift). Over-index is the signal we lead the eye to
// (green); under-index is context (muted). Emphasis is by weight + color, never
// italics (house rule). Absent inside the parity band, so the badge only ever
// marks something genuinely distinctive.
function LiftBadge({ lift }) {
  const chip = liftChip(lift);
  if (!chip) return null;
  const over = chip.dir === "over";
  return (
    <span
      className={[
        "shrink-0 text-[10px] leading-none tabular-nums font-medium",
        over ? "text-[color:var(--green)]" : "text-[color:var(--muted)]",
      ].join(" ")}
      title={undefined}
      aria-label={`${over ? "over" : "under"}-indexes ${chip.text} vs the org`}
    >
      {over ? "↑" : "↓"}{chip.text}
    </span>
  );
}

// One composition panel — the top members of a dim within the current scope,
// with a concentration headline computed by the caller. Shared by the cluster
// and MCP-server value lenses: rows render with the app's canonical entry
// treatment (label + value, EntrySparkline's share-tint + line beneath — the
// same picture the sidebar/scope columns draw) and drill the scope into
// scope ∩ member (the sidebar's own narrowing semantic). `scopeTotal` is the
// prompt total of the surrounding scope, used only to compute the top-row
// share-of-scope note. Without `onDrill` the rows are static: a caller whose
// members have nowhere to go must not offer a click that does nothing.
export function CompositionPanel({ title, data, loading, scopeTotal, onDrill, liftById, rows = COMPOSITION_ROWS, pinnedId, subLabel, onRowHover }) {
  const entries = (data?.entries ?? []).slice(0, rows);
  const distinct = data?.total ?? entries.length;
  const top = entries[0] ?? null;
  const topShare = top && scopeTotal > 0
    ? Math.min(100, Math.round((top.activity / scopeTotal) * 100))
    : null;
  return (
    <div className="min-w-0" data-comp-col>
      <div className="flex items-baseline justify-between gap-[8px] border-b border-[color:var(--line-soft)] pb-[4px]">
        <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
          {title}
          {subLabel ? <span className="text-[color:var(--green)]"> · {subLabel}</span> : null}
        </span>
        <span className={`${NOTE_CLASS} shrink-0 text-[10px]`}>
          {distinct ? `${formatCompact(distinct)} distinct` : "—"}
          {topShare != null ? ` · top ${topShare}%` : ""}
        </span>
      </div>
      {loading && !entries.length ? (
        <div className="grid gap-[5px] py-[6px]">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-[16px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
          ))}
        </div>
      ) : entries.length ? (
        <div className="grid min-w-0">
          {entries.map((e, ri) => {
            const pinned = pinnedId != null && String(e.id) === String(pinnedId);
            const Row = onDrill ? "button" : "div";
            return (
              <Row
                key={e.id}
                {...(onDrill ? { type: "button", onClick: () => onDrill(e.id, e), "aria-pressed": pinned || undefined } : {})}
                data-comp-row
                onMouseEnter={onRowHover ? () => onRowHover(ri) : undefined}
                className={[
                  "grid w-full min-w-0 gap-[1px] border-t border-[color:var(--line-soft)] py-[5px] text-left first:border-t-0",
                  onDrill ? "cursor-pointer" : "",
                  pinned
                    ? "border-l-2 border-l-[color:var(--green)] bg-[color:var(--control-bg)] pl-[6px] pr-[2px]"
                    : onDrill ? "px-[2px] hover:bg-[color:var(--control-bg)]" : "px-[2px]",
                ].join(" ")}
              >
                <span className="flex min-w-0 items-baseline justify-between gap-[8px]">
                  <span className="min-w-0 flex-1 truncate text-[11.5px] leading-[1.2] text-[color:var(--ink)]">{e.label}</span>
                  <span className="flex shrink-0 items-baseline gap-[6px]">
                    {liftById ? <LiftBadge lift={liftById.get(String(e.id))} /> : null}
                    <span className="text-[10.5px] tabular-nums text-[color:var(--muted)]">
                      {e.activity ? formatCompact(e.activity) : "–"}
                    </span>
                  </span>
                </span>
                <EntrySparkline sparkline={e.sparkline} share={e.share} />
              </Row>
            );
          })}
        </div>
      ) : (
        <span className={`${NOTE_CLASS} block py-[6px]`}>none in this window</span>
      )}
    </div>
  );
}
